import { useEffect, useMemo, useState } from 'react';
import { calculateMileage, oneWayMiles } from '@d2p-expenses/shared';
import { api, type Category, type LineItem } from '../lib/api';
import { ErrorNote, Sheet } from '../components/ui';

/**
 * The live "32.0 mi × $0.7150 = $22.88" line under the miles field is computed
 * with calculateMileage from packages/shared -- the exact function the server
 * uses on save. If these ever drift the person sees one number and is paid
 * another, and the submit-time total assertion starts rejecting valid reports.
 */
export function MileageSheet({
  open, onClose, reportId, categories, existing, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  reportId: string;
  categories: Category[];
  existing?: LineItem | null;
  onSaved: () => void;
}) {
  const mileageCategory = categories.find((c) => c.appliesToKind === 'mileage');

  // Stored miles are already doubled for a round trip; the field shows the
  // one-way figure the person originally typed. Same helper the server uses.
  const storedOneWay = existing?.miles ? oneWayMiles(existing.miles, existing.round_trip ?? false) : '';

  const [date, setDate] = useState(existing?.expense_date ?? new Date().toISOString().slice(0, 10));
  const [from, setFrom] = useState(existing?.from_location ?? '');
  const [to, setTo] = useState(existing?.to_location ?? '');
  const [miles, setMiles] = useState(storedOneWay);
  const [roundTrip, setRoundTrip] = useState(existing?.round_trip ?? false);
  const [purpose, setPurpose] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [rate, setRate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get<{ effective: { ratePerMile: string } | null }>(`/rates/mileage?date=${date}`)
      .then((d) => setRate(d.effective?.ratePerMile ?? null))
      .catch(() => setRate(null));
  }, [open, date]);

  const preview = useMemo(() => {
    if (!rate || !miles || Number(miles) <= 0) return null;
    try {
      const result = calculateMileage({ miles, ratePerMile: rate, roundTrip });
      return { miles: result.totalMiles, amount: (result.amount / 100).toFixed(2) };
    } catch {
      return null;
    }
  }, [miles, rate, roundTrip]);

  async function save() {
    if (!mileageCategory) { setError('No mileage category is configured'); return; }
    setBusy(true);
    setError(null);
    try {
      const body = {
        expenseDate: date,
        fromLocation: from,
        toLocation: to,
        miles: Number(miles).toFixed(2),
        roundTrip,
        purpose: purpose || undefined,
      };
      if (existing) await api.patch(`/line-items/${existing.id}/mileage`, body);
      else await api.post(`/reports/${reportId}/line-items/mileage`, { ...body, categoryId: mileageCategory.id });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the mileage');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!existing) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/line-items/${existing.id}`);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that trip');
    } finally {
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={existing ? 'Edit mileage' : 'Add mileage'}
      footer={
        <div className="space-y-2">
          <button type="button" className="btn-primary w-full" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save mileage'}
          </button>
          {existing ? (
            confirmingDelete ? (
              <div className="flex gap-2">
                <button type="button" className="btn-secondary flex-1" disabled={busy}
                        onClick={() => setConfirmingDelete(false)}>Keep it</button>
                <button type="button" className="btn-danger flex-1" disabled={busy}
                        onClick={() => void remove()}>{busy ? 'Deleting…' : 'Yes, delete'}</button>
              </div>
            ) : (
              <button type="button" className="btn-danger w-full" disabled={busy}
                      onClick={() => setConfirmingDelete(true)}>Delete this trip</button>
            )
          ) : null}
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="mdate">Date</label>
          <input id="mdate" type="date" className="field" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="from">From</label>
          <input id="from" className="field" list="recent-locations" value={from}
                 onChange={(e) => setFrom(e.target.value)} placeholder="Office" />
        </div>
        <div>
          <label className="label" htmlFor="to">To</label>
          <input id="to" className="field" list="recent-locations" value={to}
                 onChange={(e) => setTo(e.target.value)} placeholder="Client site" />
        </div>
        <div>
          <label className="label" htmlFor="miles">Miles (one way)</label>
          <input id="miles" type="text" inputMode="decimal" className="field text-lg font-medium"
                 value={miles} placeholder="0.0"
                 onChange={(e) => setMiles(e.target.value.replace(/[^\d.]/g, ''))} />
          {preview ? (
            <p className="mt-2 rounded-lg bg-brand-light px-3 py-2 text-sm text-brand">
              {preview.miles} mi × ${rate} = <strong>${preview.amount}</strong>
            </p>
          ) : rate === null ? (
            <p className="mt-2 text-xs text-amber-700">No mileage rate is configured for {date}.</p>
          ) : null}
        </div>

        <label className="flex min-h-11 items-center gap-3">
          <input type="checkbox" className="h-5 w-5" checked={roundTrip}
                 onChange={(e) => setRoundTrip(e.target.checked)} />
          <span>Round trip (doubles the miles)</span>
        </label>

        <div>
          <label className="label" htmlFor="mpurpose">Purpose (optional)</label>
          <input id="mpurpose" className="field" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </div>

        <ErrorNote>{error}</ErrorNote>
      </div>
    </Sheet>
  );
}
